import { useQuery } from '@tanstack/react-query';

import { health } from '../api/client';
import { formatAge, formatTs } from '../lib/format';
import { ErrorState, Spinner, StatusPill } from '../components/ui';

/**
 * `/health` — `/api/health` as a page (PLAN W3: "health-страница"). This is the
 * machine view: the exact document the external RU-side observer consumes, with
 * the per-job table for a human who landed here from the footer link.
 */
export function HealthPage() {
  const query = useQuery({ queryKey: ['health-rich'], queryFn: ({ signal }) => health.rich(signal), refetchInterval: 30_000 });

  if (query.isPending) return <Spinner label="Загружаю /api/health" />;
  if (query.isError) return <ErrorState message={String(query.error)} onRetry={() => void query.refetch()} />;

  const data = query.data;
  if (!data) return null;

  const jobs = Object.entries(data.jobs).sort(([a], [b]) => a.localeCompare(b));

  return (
    <>
      <div className="page-header">
        <div>
          <h1>Health</h1>
          <p className="page-header__sub">
            Машиночитаемый документ <code>GET /api/health</code> (тот же, что читает внешний наблюдатель) ·{' '}
            {formatTs(data.generated_at)}
          </p>
        </div>
        <StatusPill status={data.overall} />
      </div>

      <div className="header-strip">
        <span className="header-strip__label">
          sync: <StatusPill status={data.sync.state} /> {data.sync.lag_hours !== null ? `lag ${data.sync.lag_hours} ч` : ''}
        </span>
        <span className="header-strip__label">
          runner:{' '}
          {data.runner.offline ? (
            <strong>offline</strong>
          ) : (
            <>
              {data.runner.last_poll_age_s === null ? 'never polled' : `${formatAge(data.runner.last_poll_age_s)} назад`}
            </>
          )}
        </span>
        <span className="header-strip__label">алертов активно: {data.alerts.length}</span>
        <span className="header-strip__label">заглушено джоб: {data.alertsMuted}</span>
      </div>

      <section className="card card--flush">
        <div className="table-wrap">
          <table className="data" data-testid="health-jobs">
            <thead>
              <tr>
                <th>Джоба</th>
                <th>Статус</th>
                <th className="num">Возраст</th>
                <th>Причина</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(([id, job]) => (
                <tr key={id}>
                  <td className="cell-primary">{id}</td>
                  <td>
                    <StatusPill status={job.status} />
                  </td>
                  <td className="num cell-muted">{formatAge(job.age_s)}</td>
                  <td className="cell-muted">{job.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card raw-events">
        <h2 className="section-title">Сырой JSON</h2>
        <pre data-testid="health-json">{JSON.stringify(data, null, 2)}</pre>
      </section>
    </>
  );
}
